import { ManagedDomEventListeners } from '@ipfabric/ui-components/utils/dom/ManagedDomEventListeners';
import { Emitter } from './RocketAnimation/Emitter';
import { Sequence } from './RocketAnimation/Sequence';
import { Tween } from './RocketAnimation/Tween';

interface EmitterConfig {
  size: number;
  count: number;
  rate: number;
  speed: number;
  fade: number;
  angle: number;
  spread: number;
  bounceX: number;
  bounceY: number;
  windAngle: number;
  windSpeed: number;
  invert: boolean;
}

const CLOUD_CONFIG: EmitterConfig = {
  size: 75,
  count: 2,
  rate: 40,
  speed: 1,
  fade: 1,
  angle: -90,
  spread: 160,
  bounceX: 1,
  bounceY: -0.3,
  windAngle: 90,
  windSpeed: 0.18,
  invert: false,
};

const SMOKE_CONFIG: EmitterConfig = {
  size: 120,
  count: 2,
  rate: 30,
  speed: 6,
  fade: 7,
  angle: 90,
  spread: 15,
  bounceX: 1,
  bounceY: 1,
  windAngle: 90,
  windSpeed: 0,
  invert: true,
};

interface RocketAnimationProps {
  canvasElement: HTMLCanvasElement | null;
  rocketElement: HTMLElement | null;
  rocketWrapperElement: HTMLElement | null;
}

class RocketAnimation {
  private domEventListeners: ManagedDomEventListeners;
  private frameTime: number;
  private frameRate: number;
  private mouseX: number;
  private rocketY: number;

  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private rocket!: HTMLElement;
  private rocketWrapper!: HTMLElement;

  private rocketAnim!: Tween;
  private smokeAnim!: Tween;
  private cloudAnim!: Tween;

  private smoke!: Emitter;
  private cloud!: Emitter;

  private rocketPosX: number;

  constructor(props: RocketAnimationProps) {
    this.domEventListeners = new ManagedDomEventListeners();
    this.frameTime = 0;
    this.frameRate = 60;
    this.mouseX = window.innerWidth / 2;
    this.rocketY = 0;

    this.rocketPosX = 0;

    this.initElements(props);
    this.initAnimations();
    this.setupEventListeners();
  }

  private initElements(props: RocketAnimationProps): void {
    if (
      !props.canvasElement ||
      !props.rocketElement ||
      !props.rocketWrapperElement
    ) {
      throw new Error('Required DOM elements not found');
    }

    this.canvas = props.canvasElement;
    this.rocket = props.rocketElement;
    this.rocketWrapper = props.rocketWrapperElement;

    const contentRect = this.rocketWrapper.getBoundingClientRect();
    this.canvas.width = contentRect.width;
    this.canvas.height = contentRect.height;
    this.adjustRocketForFlight();

    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      throw new Error('2D context not available');
    }
    this.ctx = ctx;
  }

  private initAnimations(): void {
    this.rocketAnim = new Tween(5000, 200, 0, 'easeOutElastic');
    this.smokeAnim = new Tween(7000, 0, -this.canvas.height * 6, 'easeOutExpo');
    this.cloudAnim = new Tween(3000, 0, 1, 'linear');

    this.cloud = new Emitter(
      this.canvas,
      this.canvas.width / 2,
      this.canvas.height - 20,
      CLOUD_CONFIG,
    ).stop();

    this.smoke = new Emitter(
      this.canvas,
      this.canvas.width / 2,
      this.canvas.height,
      SMOKE_CONFIG,
    ).stop();
  }

  private setupEventListeners(): void {
    const handleMove = (clientX: number): void => {
      const canvasRect = this.canvas.getBoundingClientRect();
      this.mouseX = clientX - canvasRect.left;
    };

    const handleTouchMove = (e: TouchEvent): void =>
      // Fixed noUncheckedIndexedAccess NIM-19408
      handleMove(e.touches[0]!.clientX);
    const handleMouseMove = (e: MouseEvent): void => handleMove(e.clientX);

    this.domEventListeners.add([window, 'touchmove', handleTouchMove]);
    this.domEventListeners.add([window, 'mousemove', handleMouseMove]);
  }

  public start(): void {
    new Sequence({
      init: () => this.drawAnimation(),
      100: () => this.canvas.classList.add('show'),
      105: () => this.rocket.classList.add('show'),
      110: () => this.cloud.start(),
      200: () => this.smoke.start(),
      190: () => this.rocketAnim.start(),
    });
  }

  private drawAnimation = (time?: number): void => {
    const rocketWidth = this.rocket.clientWidth;

    const minX = rocketWidth / 2;
    const maxX = this.canvas.width - rocketWidth / 2;

    const cloudTween = this.cloudAnim.tween;
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    const targetX = Math.max(minX, Math.min(maxX, this.mouseX));
    this.rocketPosX = this.rocketPosX * 0.99 + targetX * 0.01;

    this.rocket.style.left = `${this.rocketPosX}px`;
    this.rocket.style.top = `${this.smoke.y}px`;

    this.smoke.x = this.rocketPosX;
    this.smoke.y = this.rocketY + this.smokeAnim.tween + this.rocketAnim.tween;
    this.cloud.x = this.cloud.x * 0.99 + this.rocketPosX * 0.01;

    this.cloud.prop({
      size: 60 + cloudTween * 250,
      speed: 4 + cloudTween * 4,
      rate: 70 + cloudTween * 70,
    });

    this.cloud.loop();
    this.smoke.loop();

    const tiltAngle = 25;
    const tilt = ((this.mouseX - this.smoke.x) / this.canvas.width) * tiltAngle;
    this.rocket.style.top = `${this.smoke.y}px`;
    this.rocket.style.transform = `translate3d(-50%,-75%,0) rotate3d(0,0,1,${tilt}deg)`;

    if (time !== undefined && this.frameTime) {
      this.frameRate =
        this.frameRate * 0.99 + (1000 / (time - this.frameTime)) * 0.01;
    }

    this.frameTime = time ?? 0;
    requestAnimationFrame(this.drawAnimation);
  };

  public fly(): void {
    new Sequence({
      init: () => this.prepareForFlight(),
      900: () => this.adjustCloudForFlight(),
      1000: () => this.adjustParticlesForFlight(),
      1010: () => this.smokeAnim.start(),
      1060: () => this.cloudAnim.start(),
      1100: () => this.cloud.prop({ spread: 180, fade: 1 }),
      4000: () => this.canvas.classList.add('fade'),
      5000: () => this.smoke.stop(),
      5001: () => this.cloud.stop(),
    });
  }

  private prepareForFlight(): void {
    const beforeHeight = this.canvas.height;
    const contentRect = this.rocketWrapper.getBoundingClientRect();

    this.canvas.height = contentRect.height;
    this.adjustRocketForFlight();

    this.cloud.particles = this.cloud.particles.map((p) => {
      p.pos.y += this.canvas.height - beforeHeight;
      return p;
    });

    this.smoke.y = this.rocketY + this.smokeAnim.tween + this.rocketAnim.tween;
    this.smoke.prop({
      size: 250,
      spread: 45,
      count: 8,
      speed: 7,
    });
  }

  private adjustCloudForFlight(): void {
    this.cloud.prop({
      windSpeed: 0.4,
      bounceY: -0.1,
    });
  }

  private adjustParticlesForFlight(): void {
    this.smoke.prop({
      size: 100,
      rate: 20,
      spread: 8,
      speed: 5,
      count: 5,
    });

    this.cloud.prop({
      windSpeed: 0.05,
      bounceY: -0.6,
      fade: 5,
    });
  }

  private adjustRocketForFlight(): void {
    this.rocketY = this.canvas.height * 0.6;
  }

  public resize(): void {
    const beforeHeight = this.canvas.height;
    const contentRect = this.rocketWrapper.getBoundingClientRect();

    this.canvas.width = contentRect.width;
    this.canvas.height = contentRect.height;
    this.cloud.y = this.canvas.height;

    this.cloud.particles = this.cloud.particles.map((p) => {
      p.pos.y += this.canvas.height - beforeHeight;
      return p;
    });

    this.adjustRocketForFlight();
    this.smoke.y = this.rocketY + this.smokeAnim.tween + this.rocketAnim.tween;
  }

  public stop(): void {
    this.domEventListeners.remove();
  }
}

export const rocketStart = ({
  canvasElement,
  rocketElement,
  rocketWrapperElement,
}: RocketAnimationProps): {
  fly: () => void;
  resize: () => void;
  stop: () => void;
} => {
  const animation = new RocketAnimation({
    canvasElement,
    rocketElement,
    rocketWrapperElement,
  });
  animation.start();

  return {
    fly: () => animation.fly(),
    resize: () => animation.resize(),
    stop: () => animation.stop(),
  };
};
